import { api } from "./client";

/**
 * A shift as the plant defines it, not as the PLC recorded it.
 *
 * This is what lets the dashboard know a shift exists before it has run: the
 * roll tables only gain a row once the PLC starts counting, so tonight's shift
 * is invisible until it begins.
 *
 * `start`/`end` are HH:MM on the Nashville wall clock; `days` is 0=Sun..6=Sat.
 * Names still carry a "Lamination 1 " prefix from when there was only one line
 * — the backend strips it when naming recorded shifts, so this does too.
 */
export type ShiftDefinition = {
  id: number;
  name: string;
  start: string;
  end: string;
  days: number[];
};

const stripLinePrefix = (name: string) =>
  name.replace(/^Lamination\s*\d+\s*/i, "").trim();

/** Minutes past midnight for an "HH:MM", or null if it isn't one. */
export const shiftStartMinutes = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

export const getShiftSchedule = (signal?: AbortSignal) =>
  api
    .get<{ shifts: ShiftDefinition[] }>("/settings/shifts", { signal })
    .then((r) =>
      (r.data?.shifts ?? []).map((s) => ({
        ...s,
        name: stripLinePrefix(s.name) || s.name,
      }))
    );
